import type { ApolloClient } from '@apollo/client';

import type {
  CreateOrderMutation,
  Dispensaries,
  MonerisInput,
  Coupons,
  ReservationsAvailable,
  ReservationsAvailableByOrderType,
  RewardV2,
  UserAuthInfo,
} from 'src/gql/graphql';

import React from 'react';
import { action, computed, makeObservable, observable, toJS, transaction } from 'mobx';
import reduce from 'lodash/fp/reduce';
import size from 'lodash/fp/size';
import cloneDeep from 'lodash/fp/cloneDeep';
import _ from 'lodash';
import { isBlank } from 'underscore.string';
import { Events, tracker } from 'utils/analytics';
import type { AnalyticsCart, AnalyticsOrder } from 'utils/analytics/events';
import { isGtmV2Enabled } from 'utils/analytics/helpers/gtm-v2-flag';
import { isMultiSubcategoryPurchaseLimitsEnabled } from 'state/purchase-limits-flag';

import { getStoredCartKey } from 'src/state/actions/cart';
import { formatAddress, formatAddressShort, normalizeStoredAddress } from 'shared/helpers/address';
import { integrationsByCategory } from 'shared/core/constants/integrations';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import { getCheckoutLineItemsFromOrder } from 'shared/helpers/order-line-items';
import { getTaxInclusionNote } from 'shared/helpers/products';

import {
  LOCAL_STORAGE_STORED_CART_KEY,
  MENU_TYPE_REC,
  MENU_TYPE_MED,
  PAYMENT_CASH,
  ORDER_TYPE_DELIVERY,
  ORDER_TYPE_PICKUP,
  ORDER_TYPE_CURBSIDE_PICKUP,
  ORDER_TYPE_DRIVE_THRU_PICKUP,
  defaultDurationEstimates,
  MARKETING_CAMPAIGN_PARAMS,
} from 'shared/constants';
import {
  openInfoForDispensary,
  getDefaultRewardsProgramDisplayDescription,
  getDefaultRewardsProgramDisplayEnrollment,
  getDefaultRewardsProgramDisplayName,
  requiresCustomerState,
} from 'shared/core/helpers/dispensaries';
import { checkAllLimits } from 'shared/helpers/purchase-limits';
import { maxQuantity } from 'shared/helpers/max-quantity';

import { getCartAdditionAccess, getIsAfterHoursOrderV2, getOrderingOptionsV2 } from 'utils/helpers/dispensary';
import { cartItemId } from 'utils/helpers/product';
import {
  validateInlineAuth,
  getCheckoutReservationOptions as getCheckoutReservationOptionsHelper,
  getWeightChangeDecision,
} from 'utils/helpers/cart-helpers';
import {
  productAddedToCart as analyticsProductAddedToCart,
  productRemovedFromCart as analyticsProductRemovedFromCart,
  cartDidPurchase as analyticsCartDidPurchase,
} from 'utils/ecommerce-analytics';

import CartItem from './cart-item';
import calculationActions from './cart-calc-actions/calculation-actions';
import type { CurrentAddress } from './ui';
import FeatureFlagsStore from './feature-flags';
import type { CartAddItemInput, BreakdownItem, ReceiptProduct } from './cart.types';

// ─── Local type definitions ───────────────────────────────────────────────────

type TipValue = { dollar: number; percent?: never } | { percent: number; dollar?: never };
type CouponValue = (Coupons & { dollar: number }) | (Coupons & { percent: number });

type AppliedReward = {
  id?: string | null;
  discountId?: number | null;
  copy?: string | null;
  value?: string | null;
  operator?: string | null;
  available?: boolean | null;
  brand?: string | null;
};

type CartOrder = {
  dispensary: Dispensaries | null;
  firstTimePBBUser: boolean;
  payerId: string | null;
  medicalOrder: boolean;
  paymentMethod: string;
  isGuestOrder: boolean;
  guestCustomer: Record<string, unknown> | false;
  deliveryOption: string | null;
  tipValue: TipValue | null;
  coupon: Coupons | null;
  couponValue: CouponValue | null;
  driversLicense: string | undefined;
  reservation: ReservationsAvailable | null;
  inlineAuth: InlineAuth;
  paysafeInput: PaysafeInputStore;
  appliedRewards: AppliedReward[];
  rewardsBalance: string | null | undefined;
  saveGuestInfo: boolean;
  medicalCard: MedicalCard | undefined;
  specialInstructions: string | null;
  checkoutToken: string | null;
  customerState: string | null;
  utmData: UtmData | null;
  loyaltyPointsApplied: number;
  loyaltyAppliedAsDiscount: number;
  posCustomerId: string | null;
  updateOrderToken: string | null;
  cart: Record<string, unknown> | null;
  roundupForCharityEnabled: boolean;
};

type StashedOrder = CartOrder & {
  id: string;
  orderId: string | undefined;
  isAfterHoursOrder: boolean;
  isInStorePickup: boolean;
  isCurbsidePickup: boolean;
  isDriveThruPickup: boolean;
  isDelivery: boolean;
  openInfo: OpenInfoForDispensary;
  guestInfoToSave: GuestInfoToSave;
};

type GuestInfoToSave = {
  customer?: {
    email?: string;
    password?: string;
    profile?: {
      firstName?: string;
      lastName?: string;
      phone?: string;
      birthday?: string | null;
      textNotifications?: boolean;
      emailNotifications?: boolean;
      emailOptIn?: boolean;
      photoId?: string;
      medicalCard?: {
        number?: string;
        expirationDate?: string;
        state?: string;
        photo?: string;
      };
    };
  };
};

type MedicalCard = {
  number?: string;
  expirationDate?: string;
  state?: string;
  photo?: string;
  dateOfBirth?: string;
};

type InlineAuth = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  birthdate?: string;
  password?: string;
};

type InlineAuthType = 'login' | 'signup';

type PendingAnalyticsEvent =
  | { type: 'add'; payload: Events['addedProductToCart'] }
  | { type: 'remove'; payload: Events['removedProductFromCart'] };

type MostRecentProduct = {
  id: string;
  quantity: number;
  option: string;
  additionalOption: string | undefined;
  trackerSource: string | null | undefined;
};

type MostRecentItem = {
  wasItemAlreadyInCart: boolean;
  item: InstanceType<typeof CartItem>;
};

type PaysafeInputStore = { token: string | null; zip: string | null; tokenizedAt: number | null };

/**
 * Tax existence flags per menu type.
 * - priceCart path: taxes.js assigns boolean (tax.gt(0) returns true/false directly)
 * - legacy GQL path: schema incorrectly defines these as String, so GQL serializes
 *   the boolean to "true"/"false" strings over the wire
 * Both paths are consumed via !!exists so runtime behavior is identical.
 * TODO: schema.graphql TaxExistenceInside fields should be Boolean, not String.
 */
type TaxExistenceByMenuType = {
  checkout?: boolean | string | null;
  menu?: boolean | string | null;
  pos?: boolean | string | null;
};

type TaxExistence = {
  med?: TaxExistenceByMenuType;
  rec?: TaxExistenceByMenuType;
};

/**
 * Serialized receipt from Receipt.toJSON() in receipt.js.
 * All Big.js values are converted to numbers by toJSONMapper().
 */
type CartReceipt = {
  specialsSettings?: unknown;
  taxConfig?: unknown;
  summary?: {
    subtotal: number;
    taxes: number;
    fees: number;
    rewards: number;
    discounts: number;
    incentives: number;
    mixAndMatch: number;
    total: number;
    rounding: number;
    roundupForCharity: number;
  };
  details?: {
    order?: {
      taxes?: Record<string, number>;
      fees?: Record<string, number>;
      rewards?: Record<string, unknown>;
      discounts?: Record<string, unknown>;
      incentives?: Record<string, unknown>;
      mixAndMatch?: Record<string, unknown>;
      rounding?: Record<string, unknown>;
      roundupForCharity?: Record<string, unknown>;
    };
    products?: ReceiptProduct[];
    taxBreakdown?: Record<string, number>;
  };
};

/**
 * Applied discount with restriction metadata from extractAppliedDiscounts() in price-cart-calculations.js.
 * Sourced from POS-13717 restriction fields on cartItemPrices discounts.
 */
type AppliedDiscountMetadata = {
  _id: string;
  discountId: number;
  name: string;
  amount: number;
  applicationMethod: string;
  isLoyaltyReward: boolean;
  isPaymentIncentive: boolean;
  isRestricted: boolean;
  menuDisplayName: string | null;
  menuDisplayDescription: string | null;
  menuDisplayImage: string | null;
};

/** Gamification threshold from serializeGamificationDetails() in price-cart-calculations.js. */
type GamificationThreshold = {
  thresholdType: string;
  threshold: number;
  thresholdMet: boolean;
  currentAmount: number;
  applicableProducts: Array<{ productKey: string; amount: number }>;
};

/** Gamification detail from serializeGamificationDetails() in price-cart-calculations.js. */
type GamificationDetail = {
  specialId: string;
  specialName: string;
  allThresholdsMet: boolean;
  thresholds: GamificationThreshold[];
};

/**
 * Return type of OrderComputationsAsync.compute(), which returns either priceCartComputer()
 * or serverSideComputations() response. All fields optional because the initial value is {}
 * and error paths return {}.
 *
 * Sources:
 *   - priceCartComputer return (packages/shared/order/price-cart-calculations.js lines 225–283)
 *   - serverSideCalculations GQL fields (packages/shared/graphql/calculations/mutations/server-side-calculations.gql)
 */
type CartCalculationOutput = {
  menuTotal?: number;
  cartSubtotal?: number;
  subtotal?: number;
  bottleDepositTaxCents?: number;
  cannabisTax?: number;
  tax?: number;
  deliveryFee?: number;
  credit?: number;
  mixAndMatch?: number;
  reward?: number;
  tip?: number;
  otherFees?: number;
  paymentFee?: number;
  roundupForCharityAmount?: number;
  rounding?: number;
  total?: number;
  // priceCart-only numeric fields
  loyaltyAppliedAsDiscount?: number;
  // Structured fields — present in both paths
  details?: Array<BreakdownItem | null>; // GQL returns Maybe<DetailItem> elements; priceCart via taxes() returns non-null
  lineitems?: Array<{ type?: string | null; value?: number | null; brand?: string } | null>; // GQL fields are nullable; brand is priceCart-only
  paymentIncentives?: unknown[];
  salesTaxExistence?: TaxExistence;
  cannabisTaxExistence?: TaxExistence;
  bottleDepositTaxExistence?: TaxExistence;
  receipt?: CartReceipt;
  // priceCart-only structured fields
  appliedDiscountsWithMetadata?: AppliedDiscountMetadata[];
  gamificationDetails?: GamificationDetail[];
  resolvedZoneIds?: number[];
  calculationErrors?: string[];
  // priceCart error/fallback path fields
  timedOut?: boolean;
  shouldFallback?: boolean;
  priceCartError?: string;
  coupon?: unknown;
};

type PerformCalculationsInput = {
  apolloClient: ApolloClient<object>;
  userId: string | null;
  platformType: string;
  preOrderType: string;
};

type UtmData = {
  [key: symbol]: string | undefined;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  dtm_campaign?: string;
  dtm_medium?: string;
  dtm_content?: string;
};

type CheckoutLineItem = { type: string; value: number };

type OpenInfoForDispensary = Record<
  string,
  {
    isOpen: boolean;
    isClosed: boolean;
    nextService: {
      openMoment: string | null;
      openString: string;
      closeMoment: string | null;
    };
  }
>;

// ─────────────────────────────────────────────────────────────────────────────

const utmDataSymbol = Symbol('dispensaryId');

// the shopping cart!
export default class CartStore {
  @observable isCalculating = false;
  @observable calculationTimestamp = Date.now();
  @observable cartCalculationOutput: CartCalculationOutput = {};
  @observable pendingAnalyticsEvents: PendingAnalyticsEvent[] = [];
  @observable isReady = false;
  @observable isDutchieMain = false;
  @observable isMedical = false;
  @observable paymentMethod = PAYMENT_CASH;
  @observable isFirstTimePBBUser = false;
  @observable payerId: string | null = null;
  @observable items: Record<string, CartItem | undefined> = {};
  @observable orderType: string | null = null;
  @observable dispensary: Dispensaries | null = null;
  @observable mostRecentProduct: MostRecentProduct | null = null;
  @observable mostRecentItem: MostRecentItem | null = null;
  @observable featureFlags: Partial<FeatureFlagsStore> = {};

  @observable previewMode: boolean | null = null;
  @observable openInfoForDispensary: OpenInfoForDispensary | null = null;
  @observable durationEstimates: Record<string, { lowInMinutes?: number | null; highInMinutes?: number | null }> = {
    ...defaultDurationEstimates,
  };

  @observable checkoutToken: string | null = null;

  // checkout
  @observable tempAddress = '';
  /** @deprecated — unused, use UI.tempLocation instead */
  @observable tempLocation = {};
  @observable customerState: string | null = null;
  @observable address: CurrentAddress = {};
  @observable freezeDeliveryInfo = false;
  @observable coupon: Coupons | null = null;
  @observable couponValue: CouponValue | null = null;
  @observable deliveryAreaId: string | null = null;
  @observable tipValue: TipValue | null = null;
  @observable guestInfoToSave: GuestInfoToSave = {};
  @observable guestCustomer: Record<string, unknown> | false = {};
  @observable guestCouponError = false;
  @observable saveGuestInfo = false;
  @observable stashedOrder: StashedOrder | null = null;
  @observable hasRewardsWallet = false;
  @observable rewardAuth: UserAuthInfo = {};
  @observable rewardsBalance: string | null | undefined = '';
  @observable rewardPin = '';
  @observable appliedRewards: AppliedReward[] = [];
  @observable availableRewards: Array<RewardV2 & { brand?: string | null }> = [];
  @observable rewardBrandName = '';
  @observable hasCheckedForRewards = false;
  @observable reservationsAvailableByOrderType: ReservationsAvailableByOrderType | undefined;
  @observable tempEstimate: Record<string, unknown> | null = null;
  @observable driversLicense: string | undefined;
  @observable reservation: ReservationsAvailable | null = null;
  @observable reservationType = '';
  @observable medicalCard: MedicalCard | undefined;
  @observable inlineAuth: InlineAuth = {};
  @observable inlineAuthType: InlineAuthType = `login`;
  @observable paysafeInput: PaysafeInputStore = {
    token: null,
    zip: null,
    tokenizedAt: null,
  };
  @observable utmData: UtmData | null = null;
  @observable hypurAccessToken: string | null = null;
  @observable monerisInput: MonerisInput = {};
  @observable autoCheckoutAfterBankLinkReconnect = false;
  @observable calculationIndex = 0;
  // these are in dollars
  @observable loyaltyPointsApplied = 0;

  @observable loyaltyAppliedAsDiscount = 0;

  @observable posCustomerId: string | null = null;

  @observable specialInstructions: string | null = null;
  @observable destinationTerminal: string | null = null;
  @observable isKioskOrder = false;
  @observable isAnonymousKioskCheckout = false;
  @observable anonymousOrderId = '';
  @observable serverOrderId = '';
  @observable updateOrderToken: string | null = null;
  // dutchiePay
  @observable paymentMethodId = '';

  // Temporary reporting obj for special hours scheduling data
  @observable schedulingData: Record<string, unknown> | null = null;
  @observable bypassCanadaDeliveryTax = false;
  @observable kioskProUserOrder = false;

  @observable discountGamificationDetails: GamificationDetail[] = [];

  @observable charityFeeDonationId: number | null = null;
  @observable charityRoundupSelected = false;

  // Refs for imperative save calls from on-click-submit
  paysafeFormRef: React.RefObject<unknown>;
  medicalComponentRef: React.RefObject<{ onSave: () => Promise<void> }>;
  orderTypeComponentRef: React.RefObject<{ onSave: () => Promise<void> }>;
  scheduledOrderingRef: React.RefObject<{ handleSave: () => Promise<void> }>;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  constructor(isDutchieMain: boolean, FeatureFlags: Partial<FeatureFlagsStore> = {}) {
    makeObservable(this);

    this.isDutchieMain = isDutchieMain;
    this.featureFlags = FeatureFlags;

    // these refs are necessary for the auto-save feature
    // for checkout sections. Referenced in on-click-submit.js
    this.paysafeFormRef = React.createRef();
    this.medicalComponentRef = React.createRef();
    this.orderTypeComponentRef = React.createRef();
    this.scheduledOrderingRef = React.createRef();

    const persistedMedicalCard = getPersistedValue(`medicalCard`, false);
    this.setMedicalCard(persistedMedicalCard);

    // Normalize stale addresses where city was set from MapBox's "place"
    // instead of "locality" (e.g., "New York" instead of "Brooklyn").
    // Done in constructor so the address is correct before any component renders.
    // Writes to both storages since we don't know which one usePersistedValue will read from.
    // This can probably be removed after a while since most stashed addresses will now be corrected.
    const storedAddress = getPersistedValue(`address`);
    if (storedAddress) {
      const normalized = normalizeStoredAddress(storedAddress);
      if (normalized !== storedAddress) {
        this.address = normalized;
        setPersistedValue(`address`, normalized, false);
        setPersistedValue(`address`, normalized, true);
      }
    }

    const storedCart = getPersistedValue(LOCAL_STORAGE_STORED_CART_KEY, {});

    // For DutchieMain, we can get the delivery area ID immediately
    if (this.isDutchieMain) {
      const key = getStoredCartKey(null, true);
      const storedCartData = _.isObject(storedCart) ? storedCart[key] : undefined;
      const persistedDeliveryAreaId = storedCartData?.deliveryAreaId;
      if (persistedDeliveryAreaId) {
        this.deliveryAreaId = persistedDeliveryAreaId;
      }
    }
    // For embedded menus, we'll need to wait until dispensary is set
    // This will be handled in setDispensary method
  }

  @action.bound
  setGuestFromPersisted(): void {
    this.guestCustomer = getPersistedValue(`guestCustomer`, {});
    this.customerState = getPersistedValue(`customerState`, null);
  }

  @action
  clearPersistedGuestCustomer(): void {
    setPersistedValue(`guestCustomer`, {});
    setPersistedValue(`customerState`, null);
  }

  @action.bound
  setDeliveryAreaId(deliveryAreaId: string | null): void {
    this.deliveryAreaId = deliveryAreaId;

    const storedCart = getPersistedValue(LOCAL_STORAGE_STORED_CART_KEY, {});
    const { currentStoredCartKey } = this;

    if (currentStoredCartKey) {
      storedCart[currentStoredCartKey] = {
        ...storedCart[currentStoredCartKey],
        deliveryAreaId,
      };
      setPersistedValue(LOCAL_STORAGE_STORED_CART_KEY, storedCart);
    }
  }

  @action
  setAutoCheckoutAfterBankLinkReconnect(status: boolean): void {
    this.autoCheckoutAfterBankLinkReconnect = status;
  }

  @action
  setFreezeDeliveryInfo(value: boolean): void {
    this.freezeDeliveryInfo = value;
  }

  @action.bound
  addItem(dispensary: Dispensaries | null, item: CartAddItemInput, id: string = cartItemId(item)): CartItem | null {
    if (this.dispensary !== dispensary) {
      this.setDispensary(dispensary);
    }

    const newCartItem = new CartItem(this, item, id);
    const existingItem = this.items[id];

    if (existingItem) {
      existingItem.add(newCartItem);
    } else {
      this.items[id] = newCartItem;
    }

    this.mostRecentProduct = {
      id,
      quantity: item.quantity,
      option: item.option,
      additionalOption: item.additionalOption,
      trackerSource: item.trackerSource,
    };

    const addedCartItem = existingItem ?? newCartItem;

    this.mostRecentItem = {
      wasItemAlreadyInCart: !!existingItem,
      item: addedCartItem,
    };

    return addedCartItem;
  }

  @action.bound
  analyticsProductAddedToCart(item: CartAddItemInput): void {
    if (isGtmV2Enabled()) {
      tracker.setContext({
        activeCart: ({
          ...cloneDeep(this),
          subtotal: this.costBreakdown.total,
        } as unknown) as AnalyticsCart,
        activeDispensary: this.dispensary,
        activeOrder: (this.order as unknown) as AnalyticsOrder,
      });
    } else {
      tracker.setContext({
        activeCart: ({
          ...cloneDeep(this),
          subtotal: this.costBreakdown.subtotal,
        } as unknown) as AnalyticsCart,
        activeDispensary: this.dispensary,
        activeOrder: (this.order as unknown) as AnalyticsOrder,
      });
    }
    analyticsProductAddedToCart(item, cloneDeep(this));
  }

  @action.bound
  getItemQuantityByProductId(productId: string): number {
    const itemInCart = this.getItemByProductId(productId);

    return itemInCart ? itemInCart.quantity : 0;
  }

  @action.bound
  getItemByProductId(productId: string): CartItem | null {
    const itemInCart = _.find(Object.entries(this.items), (item) => item[1]?.id === productId);
    return itemInCart ? itemInCart[1] ?? null : null;
  }

  @action.bound
  setAnonymousOrderId(anonymousOrderId: string): void {
    this.anonymousOrderId = anonymousOrderId;
  }

  @action.bound
  setDispensary(dispensary: Dispensaries | null): void {
    this.dispensary = dispensary;

    // Restore delivery area ID from stored cart for embedded menus
    if (!this.isDutchieMain && dispensary?.id && !this.deliveryAreaId) {
      const storedCart = getPersistedValue(LOCAL_STORAGE_STORED_CART_KEY, {});
      const persistedDeliveryAreaId = storedCart[dispensary.id]?.deliveryAreaId;
      if (persistedDeliveryAreaId) {
        this.deliveryAreaId = persistedDeliveryAreaId;
      }
    }
  }

  @action.bound
  setDestinationTerminal = (destinationTerminal: string | null): void => {
    this.destinationTerminal = destinationTerminal;
  };

  @action.bound
  setIsAnonymousKioskCheckout(isAnonymousKisokCheckout: boolean): void {
    this.isAnonymousKioskCheckout = isAnonymousKisokCheckout;
  }

  @action.bound
  setIsDutchieMain = (isDutchieMain: boolean): void => {
    this.isDutchieMain = isDutchieMain;
  };

  @action.bound
  clearMostRecentProduct(): void {
    this.mostRecentProduct = null;
  }

  @action.bound
  applyReward(award: AppliedReward): void {
    this.appliedRewards = [];
    this.appliedRewards.push(award);
  }

  @action.bound
  setInlineAuthType(type: InlineAuthType): void {
    this.inlineAuthType = type;
  }

  // dutchiePay
  @action.bound
  setPaymentMethodId(paymentMethodId: string): void {
    this.paymentMethodId = paymentMethodId;
  }

  @action.bound
  setIsFirstTimePBBUser(isFirstTimePBBUser = false): void {
    this.isFirstTimePBBUser = isFirstTimePBBUser;
  }

  @action.bound
  setPayerId(payerId: string | null = null): void {
    this.payerId = payerId;
  }

  @action.bound
  undoApplyReward(rewardId: number | string): void {
    this.appliedRewards = _.dropWhile(
      this.appliedRewards,
      (reward) => reward.id === rewardId || reward.discountId === rewardId
    );
  }

  @action.bound
  changeItemWeight(
    item: CartItem,
    option: string,
    showErnie: (message: string) => void,
    customerState: string | null
  ): void {
    const itemsSnapshot = toJS(this.items);
    const decision = getWeightChangeDecision({
      item,
      option,
      isKioskOrder: this.isKioskOrder,
      existingItems: itemsSnapshot,
      isMedical: this.isMedical,
      dispensary: this.dispensary,
      customerState,
    });

    if (decision.action === 'error') {
      showErnie(decision.message);
      return;
    }

    this.addItem(this.dispensary, decision.newItem);
    this.removeItem(item.key);
    this.restoreItemPositions({ itemsSnapshot, oldItemKey: item.key, newItemKey: decision.newItem.key });
  }

  @action.bound
  restoreItemPositions({
    itemsSnapshot,
    oldItemKey,
    newItemKey,
  }: {
    itemsSnapshot: Record<string, CartItem | undefined>;
    oldItemKey: string;
    newItemKey: string;
  }): void {
    const snapshotKeyCount = _.keys(itemsSnapshot).length;
    const postSnapshotKeyCount = _.keys(this.items).length;
    if (snapshotKeyCount !== postSnapshotKeyCount) {
      return;
    }

    const oldItemPosition = _.findIndex(_.keys(itemsSnapshot), (key) => key === oldItemKey);
    const restoredItemOrder = _.without(_.keys(toJS(this.items)), newItemKey);
    restoredItemOrder.splice(oldItemPosition, 0, newItemKey);

    _.forEach(restoredItemOrder, (key) => {
      const itemToReinsert = this.items[key];

      this.removeItem(key);
      if (itemToReinsert) {
        this.addItem(this.dispensary, itemToReinsert);
      }
    });
  }

  @action.bound
  removeItem(entryId: string): void {
    const item = this.items[entryId];
    analyticsProductRemovedFromCart(item, cloneDeep(this));

    if (item) {
      if (isGtmV2Enabled()) {
        const itemData = item.toJSON ? item.toJSON() : { ...item };
        this.addPendingAnalyticsEvent({ type: 'remove', payload: itemData });
      } else {
        tracker.removedProductFromCart(item);
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this.items[entryId];

    if (this.mostRecentProduct?.id === entryId) {
      this.clearMostRecentProduct();
    }

    if (_.isEmpty(this.items)) {
      this.orderType = null;
    }

    this.mostRecentItem = null;
  }

  @action.bound
  setOrderType(option: string): void {
    this.orderType = option;
  }

  @action.bound
  setMenuType(menuType = MENU_TYPE_REC): void {
    this.isMedical = menuType !== MENU_TYPE_REC;
  }

  // This function is used when changing menu types
  // specifically from med -> rec, as rec limits are lower then med limits.
  // Product wants the excess quantity to be taken off the bottom
  // the item quantities are added one by one and will stop if a limit is detected
  @action.bound
  applyQuantityLimits(pricingType: string, customerState: string | null): void {
    if (_.isEmpty(this.items)) {
      return;
    }
    const isMedical = pricingType === MENU_TYPE_MED;

    const reversedCart = _.keys(this.items).reverse();

    _.forEach(reversedCart, (key) => {
      const maxLimit = maxQuantity(
        key,
        this.items,
        isMedical,
        { ...this.order, customerState },
        isMultiSubcategoryPurchaseLimitsEnabled(this.dispensary?.id)
      );

      if (maxLimit > 0) {
        const cartItem = this.items[key];
        if (cartItem) {
          cartItem.quantity = maxLimit;
        }
      } else {
        this.removeItem(key);
      }
    });
  }

  @action.bound
  changePricingType = (pricingType = MENU_TYPE_REC): void => {
    const medicalOrder = pricingType === MENU_TYPE_MED;

    if (!_.isEmpty(this.items)) {
      _.forEach(this.items, (item, id) => {
        if (!medicalOrder && item?.product.medicalOnly) {
          this.removeItem(id);
        }
        if (medicalOrder && item?.product.recOnly) {
          this.removeItem(id);
        }
      });
    }

    this.setMenuType(pricingType);
    this.isMedical = medicalOrder;
  };

  @action.bound
  decrementCartItemQuantityByOne(itemKey: string): void {
    const item = this.items[itemKey];
    if (!item) return;
    if (item.quantity === 1) {
      this.removeItem(itemKey);
      return;
    }

    if (isGtmV2Enabled()) {
      const itemData = item.toJSON();
      const cartSnapshot = cloneDeep(this);
      item.quantity -= 1;

      tracker.setContext({
        activeCart: ({
          ...cloneDeep(this),
          subtotal: this.costBreakdown.total,
        } as unknown) as AnalyticsCart,
        activeDispensary: this.dispensary,
        activeOrder: (this.order as unknown) as AnalyticsOrder,
      });

      this.addPendingAnalyticsEvent({ type: 'remove', payload: { ...itemData, quantity: 1 } });
      analyticsProductRemovedFromCart({ ...itemData, quantity: 1 }, cartSnapshot);
    } else {
      item.quantity -= 1;
    }
  }

  @action.bound
  changeItemQuantity(
    entryId: string,
    quantity: number,
    showErnie: (message: string) => void,
    customerState: string | null,
    handleAfterQuantityChange: () => void = () => undefined
  ): void {
    const limitsCheck = checkAllLimits({
      newItemId: entryId,
      itemBeingAdded: { ..._.clone(this.items[entryId]), quantity },
      existingItems: this.items,
      medical: this.isMedical,
      dispensary: this.dispensary,
      changingQuantity: true,
      customerState,
      useMultiSubcategories: isMultiSubcategoryPurchaseLimitsEnabled(this.dispensary?.id),
    });

    if (!limitsCheck.withinLimits) {
      showErnie(limitsCheck.message ?? '');
      return;
    }

    const itemToChange = this.items[entryId];

    if (itemToChange) {
      if (isGtmV2Enabled()) {
        const previousQuantity = itemToChange.quantity;
        const itemData = itemToChange.toJSON();
        const cartSnapshot = cloneDeep(this);
        itemToChange.quantity = quantity;

        tracker.setContext({
          activeCart: ({
            ...cloneDeep(this),
            subtotal: this.costBreakdown.total,
          } as unknown) as AnalyticsCart,
          activeDispensary: this.dispensary,
          activeOrder: (this.order as unknown) as AnalyticsOrder,
        });

        const quantityDiff = quantity - previousQuantity;
        if (quantityDiff > 0) {
          const analyticsPayload = { ...itemData, quantity: quantityDiff };
          this.addPendingAnalyticsEvent({ type: 'add', payload: analyticsPayload });
          analyticsProductAddedToCart(analyticsPayload, cartSnapshot);
        } else if (quantityDiff < 0) {
          const analyticsPayload = { ...itemData, quantity: Math.abs(quantityDiff) };
          this.addPendingAnalyticsEvent({ type: 'remove', payload: analyticsPayload });
          analyticsProductRemovedFromCart(analyticsPayload, cartSnapshot);
        }
      } else {
        itemToChange.quantity = quantity;
      }

      this.mostRecentProduct = {
        id: itemToChange.key,
        quantity: itemToChange.quantity,
        option: itemToChange.option,
        additionalOption: itemToChange.additionalOption,
        trackerSource: itemToChange.trackerSource,
      };

      handleAfterQuantityChange();
    }
  }

  @action.bound
  setPaymentMethod(method: string): void {
    this.paymentMethod = method;
  }

  @action.bound
  updateProduct(entryId: string, itemData: CartItem): void {
    const item = this.items[entryId];
    if (item) {
      item.product = itemData.product;
      item.quantity = itemData.quantity;
      item.option = itemData.option;
    }
  }

  @action.bound
  hasDeliveryAddress({ location = this.currentAddress }: { location?: CurrentAddress } = {}): boolean {
    const addressLine1 = location?.ln1 || '';
    const hasAddressLine = !_.isEmpty(_.trim(addressLine1)) && /^\d+$/.test(addressLine1.split(' ')[0]);
    const hasLocationLng = location?.lng;
    const noDeliveryAddress = !hasLocationLng || !hasAddressLine;

    if (noDeliveryAddress) {
      return false;
    }
    return true;
  }

  @action.bound
  updateAddress({
    location,
    residentialStatus = 'UNVERIFIED',
  }: {
    location: CurrentAddress;
    residentialStatus?: string;
  }): void {
    if (location?.geometry?.coordinates) {
      const [lng, lat] = location.geometry.coordinates;

      location = { ...location, lng, lat };
    }

    this.address = { ...location, residentialStatus };
    setPersistedValue(`address`, location);
  }

  @action.bound
  setAddress(address: CurrentAddress): void {
    this.address = address;
  }

  @action.bound
  resetAddress(): void {
    this.address = {};
  }

  @action.bound
  clearOrder(): void {
    const storedCart = getPersistedValue(LOCAL_STORAGE_STORED_CART_KEY, {});
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete storedCart[this.currentStoredCartKey ?? ''];

    if (!this.dispensary?.featureFlags?.hideStoreHours) {
      this.tempEstimate = {
        type: this.isKioskOrder ? `wait` : this.orderType,
        estimate: this.isKioskOrder ? `5 - 7 min` : this.durationEstimates[this.orderType ?? ''],
      };
    } else {
      this.tempEstimate = null;
    }

    this.checkoutToken = null;
    this.updateOrderToken = null;
    this.items = {};
    this.mostRecentItem = null;
    this.dispensary = null;
    this.orderType = null;
    this.deliveryAreaId = null;
    this.specialInstructions = '';

    this.isKioskOrder = false;
    this.utmData = null;

    this.guestInfoToSave = {};
    if (this.guestCustomer) {
      this.guestCustomer = {};
    }
    this.guestCouponError = false;
    this.saveGuestInfo = false;
    this.loyaltyPointsApplied = 0;

    this.clearPersistedGuestCustomer();
    this.removeCoupon();
    this.removeTip();
    this.clearMostRecentProduct();
    this.clearRewards();
    this.clearReservation();
    this.clearCharityRoundup();
    this.clearPendingAnalyticsEvents();

    setPersistedValue(LOCAL_STORAGE_STORED_CART_KEY, storedCart);
  }

  @action.bound
  analyticsCartPurchase(
    _orders: unknown,
    serverOrder: NonNullable<CreateOrderMutation['createOrderV2']>['order'],
    _dispensary: unknown
  ): void {
    analyticsCartDidPurchase(cloneDeep(this), serverOrder);
  }

  @action.bound
  clearRewards(): void {
    this.rewardAuth = {};
    this.hasCheckedForRewards = false;
    this.availableRewards = [];
    // Reset only if there are existing items.
    if (this.appliedRewards.length) {
      this.appliedRewards = [];
    }
    this.hasRewardsWallet = false;
    this.rewardBrandName = '';
    this.rewardsBalance = '';
    this.rewardPin = '';
    this.loyaltyPointsApplied = 0;
  }

  @action.bound
  updateCustomerState(customerState: string | null): void {
    this.customerState = customerState;
    setPersistedValue(`customerState`, customerState);
  }

  @action.bound
  updateGuestCustomer(property: string, value: unknown): void {
    if (!this.guestCustomer) {
      this.guestCustomer = {};
    }

    // If user changes their phone number, we need to reset their rewards info.
    if (property === 'phone') {
      this.clearRewards();
    }

    this.guestCustomer[property] = value;
    setPersistedValue(`guestCustomer`, this.guestCustomer);
  }

  @action.bound
  removeTip(): void {
    this.tipValue = null;
  }

  @action.bound
  applyTip(type: string, value: string): void {
    if (type === 'percent') {
      this.tipValue = { percent: parseFloat(value) };
    } else if (type === 'dollar') {
      this.tipValue = { dollar: parseFloat(value) };
    }
  }

  @action.bound
  applyCouponCode(coupon: Coupons): void {
    const { fixedDiscountInCents, percentDiscount } = coupon;
    if (percentDiscount) {
      this.couponValue = { ...coupon, percent: percentDiscount / 100 };
    } else if (fixedDiscountInCents) {
      this.couponValue = { ...coupon, dollar: fixedDiscountInCents / 100 };
    }

    this.coupon = coupon;

    if (this.isGuestOrder) {
      this.saveGuestInfo = true;
      this.guestCouponError = true;
    }
  }

  @action.bound
  setSpecialInstructions(value: string | null): void {
    this.specialInstructions = value;
  }

  @action.bound
  removeCoupon(): void {
    this.coupon = null;
    this.couponValue = null;
  }

  @action.bound
  setDriversLicenseNumber(number: string): void {
    this.driversLicense = number;
  }

  @action.bound
  setReservation(reservation: ReservationsAvailable | null): void {
    this.reservation = reservation;
  }

  @action.bound
  setReservationType(reservationType: string): void {
    this.reservationType = reservationType;
  }

  @action.bound
  setSchedulingData(schedulingData: Record<string, unknown> | null): void {
    this.schedulingData = schedulingData;
  }

  @action.bound
  clearMedicalCard(): void {
    this.medicalCard = {};
    setPersistedValue(`medicalCard`, {});
  }

  @action.bound
  setIsMedical(isMedical: boolean): void {
    this.isMedical = isMedical;
  }

  @action.bound
  setMedicalCard(medicalCard: MedicalCard | false = false): void {
    if (medicalCard && !_.isEmpty(medicalCard)) {
      this.isMedical = true;
      const { number = '', expirationDate = '', state = '', dateOfBirth = '' } = medicalCard;
      transaction(() => {
        this.setMedicalCardNumber(number);
        this.setMedicalCardExpiration(expirationDate);
        this.setMedicalCardState(state);
        this.setMedicalCardDateOfBirth(dateOfBirth);
        setPersistedValue(`medicalCard`, toJS(this.medicalCard));
      });
    }
  }

  @action.bound
  setMedicalCardNumber(number: string): void {
    const card = this.medicalCard || {};
    card.number = number;
    this.medicalCard = card;
  }

  @action.bound
  setMedicalCardExpiration(expirationDate: string): void {
    const card = this.medicalCard || {};
    card.expirationDate = expirationDate;
    this.medicalCard = card;
  }

  @action.bound
  setMedicalCardState(state: string): void {
    const card = this.medicalCard || {};
    card.state = state;
    this.medicalCard = card;
  }

  @action.bound
  setMedicalCardPhoto(photo: string): void {
    const card = this.medicalCard || {};
    card.photo = photo;
    this.medicalCard = card;
  }

  @action.bound
  setMedicalCardDateOfBirth(dateOfBirth: string): void {
    const card = this.medicalCard || {};
    card.dateOfBirth = dateOfBirth;
    this.medicalCard = card;
  }

  @action
  setCampaignData = (params: Partial<UtmData>, dispensaryId: string): void => {
    if (!dispensaryId) {
      return;
    }

    const marketingCampaignParams = _.pick(params, MARKETING_CAMPAIGN_PARAMS);

    // When navigating to a different page within the same dispensary, the utm params will be dropped.
    // This ensures we persist the utm params in state when we get an empty params obj while viewing the same dispo.
    if (_.isEmpty(marketingCampaignParams) && dispensaryId === this.utmData?.[utmDataSymbol]) {
      return;
    }

    this.utmData = marketingCampaignParams;

    // Using a Symbol here to keep this from being normally iterated.
    // The primary impact here is that it doesn't get sent over GraphQL.
    this.utmData[utmDataSymbol] = dispensaryId;
  };

  @action.bound
  updateInlineAuth(field: keyof InlineAuth, value: string): void {
    _.set(this.inlineAuth, field, value);
  }

  @action.bound
  validateAuth(): ReturnType<typeof validateInlineAuth> {
    return validateInlineAuth({
      inlineAuthType: this.inlineAuthType,
      inlineAuth: _.get(this.order, 'inlineAuth', {}),
      dispensaryState: this.order.dispensary?.location?.state ?? '',
      medicalOrder: this.order.medicalOrder,
      isMobile: window.innerWidth < 768,
    });
  }

  @action.bound
  clearReservation(): void {
    this.reservation = null;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @action.bound
  performCalculations = _.debounce(
    async ({
      apolloClient,
      userId,
      platformType,
      preOrderType,
    }: PerformCalculationsInput): Promise<CartCalculationOutput | undefined> => {
      this.calculationIndex += 1;
      return calculationActions.performCalculations({
        cart: this,
        apolloClient,
        userId,
        platformType,
        preOrderType,
      });
    },
    150
  );

  @action.bound
  setIsCalculating(isCalculating: boolean): void {
    this.isCalculating = isCalculating;
  }

  @action.bound
  setCalculationOutput(output: CartCalculationOutput): void {
    this.cartCalculationOutput = output;
    this.loyaltyPointsApplied = output?.loyaltyAppliedAsDiscount ?? this.loyaltyPointsApplied;
  }

  @action.bound
  addPendingAnalyticsEvent(event: PendingAnalyticsEvent): void {
    this.pendingAnalyticsEvents.push(event);
  }

  @action.bound
  clearPendingAnalyticsEvents(): void {
    this.pendingAnalyticsEvents = [];
  }

  @action.bound
  setCheckoutToken(token: string | null): void {
    this.checkoutToken = token;
  }

  @action.bound
  setServerOrderId(serverOrderId: string): void {
    this.serverOrderId = serverOrderId;
  }

  @action.bound
  setUpdateOrderToken(token: string | null): void {
    this.updateOrderToken = token;
  }

  @action.bound
  setBypassCanadaDeliveryTax(bool: boolean): void {
    this.bypassCanadaDeliveryTax = bool;
  }

  @action.bound
  setKioskProUserOrder(bool: boolean): void {
    this.kioskProUserOrder = bool;
  }

  @action.bound
  setDiscountGamification(gamificationDetails: GamificationDetail[]): void {
    this.discountGamificationDetails = gamificationDetails;
  }

  @action.bound
  setCharityFeeDonationId(charityFeeDonationId: number | null): void {
    this.charityFeeDonationId = charityFeeDonationId;
  }

  @action.bound
  setCharityRoundupSelected(selected: boolean): void {
    this.charityRoundupSelected = selected;
  }

  @action.bound
  clearCharityRoundup(): void {
    this.charityFeeDonationId = null;
    this.charityRoundupSelected = false;
  }

  /* ********************************************** */
  /* *      end of actions start of computed      * */

  /* ********************************************** */

  @computed
  get currentStoredCartKey(): string | null {
    return getStoredCartKey(this.dispensary?.id, this.isDutchieMain);
  }

  @computed get openInfo(): OpenInfoForDispensary {
    return openInfoForDispensary(this.dispensary);
  }

  @computed get cartAdditionAccess(): string {
    if (this.dispensary) {
      return getCartAdditionAccess(this.dispensary, { previewMode: this.previewMode }, this.isKioskOrder);
    }
    return `normal`;
  }

  @computed get isOrderingPaused(): boolean {
    if (this.dispensary) {
      return !!this.dispensary.ordersArePaused;
    }

    return false;
  }

  @computed get isAfterHoursOrder(): ReturnType<typeof getIsAfterHoursOrderV2> {
    return getIsAfterHoursOrderV2({
      dispensary: this.dispensary,
      isDelivery: this.isDelivery,
      isInStorePickup: this.isInStorePickup,
      isCurbsidePickup: this.isCurbsidePickup,
      isDriveThruPickup: this.isDriveThruPickup,
      openInfo: this.openInfo,
    });
  }

  @computed get orderOptions(): ReturnType<typeof getOrderingOptionsV2> {
    return getOrderingOptionsV2(this.dispensary, this.openInfo, this.costBreakdown);
  }

  @computed get isDelivery(): boolean {
    return this.orderType === ORDER_TYPE_DELIVERY;
  }

  @computed get isPickup(): boolean {
    return _.includes([ORDER_TYPE_PICKUP, ORDER_TYPE_CURBSIDE_PICKUP, ORDER_TYPE_DRIVE_THRU_PICKUP], this.orderType);
  }

  @computed get isInStorePickup(): boolean {
    return this.orderType === ORDER_TYPE_PICKUP;
  }

  @computed get isCurbsidePickup(): boolean {
    return this.orderType === ORDER_TYPE_CURBSIDE_PICKUP;
  }

  @computed get isDriveThruPickup(): boolean {
    return this.orderType === ORDER_TYPE_DRIVE_THRU_PICKUP;
  }

  @computed
  get simplifiedOrderType(): string | null {
    if (this.isPickup) {
      return ORDER_TYPE_PICKUP;
    }
    if (this.isDelivery) {
      return ORDER_TYPE_DELIVERY;
    }
    return null;
  }

  @computed
  get price(): number {
    return reduce((total, product: CartItem) => total + product.price, 0, this.items);
  }

  @computed
  get requiresCustomerState(): boolean {
    if (this.isKioskOrder) {
      return false;
    }
    return requiresCustomerState(this.dispensary);
  }

  @computed
  get itemCount(): number {
    return reduce((total, item: CartItem) => total + item.quantity, 0, this.items);
  }

  @computed
  get isEmpty(): boolean {
    return this.itemCount === 0 && this.subtotal === 0;
  }

  @computed
  get costBreakdown(): CartCalculationOutput {
    return this.cartCalculationOutput || {};
  }

  @computed
  get checkoutLineItems(): CheckoutLineItem[] {
    const hideDiscounts = !!this.dispensary?.storeSettings?.hideDiscountsFromConsumer;

    return getCheckoutLineItemsFromOrder(this.costBreakdown, {
      hideDiscounts,
    });
  }

  @computed
  get destinationTaxRateSum(): number {
    if (this.orderType !== 'delivery') {
      return 0;
    }

    const dispensaryTaxes = this.dispensary?.taxConfig?.taxes || [];
    const destinationTaxes = _.filter(dispensaryTaxes, (tax) => tax != null && !_.isNull(tax.destinationRate));
    return _.sumBy(destinationTaxes, 'destinationRate');
  }

  @computed
  get subtotal(): number {
    return this.costBreakdown.menuTotal || 0;
  }

  @computed
  get orderSubtotal(): number {
    return this.costBreakdown.subtotal || 0;
  }

  @computed
  get cartSubtotal(): number {
    return this.costBreakdown.menuTotal || 0;
  }

  @computed get subtotalMinusDiscounts(): number {
    const subtotal = this.costBreakdown.subtotal ?? 0;
    const credit = this.costBreakdown.credit ?? 0;
    const reward = this.costBreakdown.reward ?? 0;
    return subtotal - credit - reward;
  }

  @computed
  get cart(): Record<string, CartItem | undefined> | null {
    if (size(this.items) === 0) {
      return null;
    }

    return this.items;
  }

  @computed
  get discountToCartApplied(): boolean {
    return _.some(this.items, (item) => item?.discountToCartApplied ?? false);
  }

  @computed
  get appliedOffers(): string[] {
    return _.uniq(
      _.compact(
        _.reduce(
          this.costBreakdown.details || [],
          (discountsToCartOffers, detail) => [
            ...discountsToCartOffers,
            ..._.map(detail?.bogoSavings?.individual || [], ({ specialId }) => specialId),
          ],
          []
        )
      )
    );
  }

  // POS-13717: Get applied discounts with restriction metadata from price cart response
  @computed
  get appliedDiscountsWithMetadata(): AppliedDiscountMetadata[] {
    return this.costBreakdown?.appliedDiscountsWithMetadata || [];
  }

  @computed
  get mostRecentCartItem(): CartItem | undefined {
    return this.mostRecentProduct?.id ? this.items[this.mostRecentProduct.id] : undefined;
  }

  @computed
  get order(): CartOrder {
    const cart: Record<string, unknown> | null = this.cart ? {} : null;
    if (this.cart && cart) {
      _.forEach(_.keys(this.cart), (key) => {
        const value = this.cart?.[key];
        if (key && value) {
          cart[key] = toJS(value.toJSON());
        }
      });
    }

    return {
      dispensary: this.dispensary
        ? toJS({
            ...this.dispensary,
            useDartDiscountsPath: this.dispensary?.storeSettings?.enableLLxSaleDiscountSync,
          })
        : this.dispensary,
      firstTimePBBUser: this.isFirstTimePBBUser,
      payerId: this.payerId,
      medicalOrder: this.isMedical,
      paymentMethod: this.paymentMethod,
      isGuestOrder: this.isGuestOrder,
      guestCustomer: this.guestCustomer,
      deliveryOption: this.orderType, // FIXME not saving curbside, drive thru
      tipValue: this.tipValue,
      coupon: this.coupon,
      couponValue: this.couponValue,
      driversLicense: this.driversLicense,
      reservation: this.reservation,
      inlineAuth: this.inlineAuth,
      paysafeInput: this.paysafeInput,
      appliedRewards: this.appliedRewards,
      rewardsBalance: this.rewardsBalance,
      saveGuestInfo: this.saveGuestInfo,
      medicalCard: this.medicalCard,
      specialInstructions: this.specialInstructions,
      checkoutToken: this.checkoutToken,
      customerState: this.customerState,
      utmData: this.utmData,
      loyaltyPointsApplied: this.loyaltyPointsApplied,
      loyaltyAppliedAsDiscount: this.loyaltyAppliedAsDiscount,
      posCustomerId: this.posCustomerId,
      updateOrderToken: this.updateOrderToken,
      cart,
      roundupForCharityEnabled: this.charityRoundupSelected && !!this.charityFeeDonationId,
    };
  }

  @computed
  get taxInclusionNote(): string {
    const keys = _.keys(this.items);
    if (!keys.length) {
      return '';
    }
    const { product } = this.items[keys[0]] ?? {};
    if (!product) {
      return '';
    }
    return getTaxInclusionNote({ product, dispensary: this.dispensary, medical: this.isMedical });
  }

  @computed
  get isGuestOrder(): boolean {
    // Orders placed by logged-in consumers on Kiosk Pro should not be marked as guest orders
    if (this.kioskProUserOrder) {
      return false;
    }

    return !this.dispensary?.storeSettings?.disableGuestCheckout && !!this.guestCustomer;
  }

  @computed
  get currentAddress(): CurrentAddress {
    return this.address;
  }

  @computed
  get checkoutReservationOptions(): ReturnType<typeof getCheckoutReservationOptionsHelper> {
    const storedCart = getPersistedValue(LOCAL_STORAGE_STORED_CART_KEY, {});
    const deliveryAreaId = this.deliveryAreaId || storedCart[this.currentStoredCartKey ?? '']?.deliveryAreaId;
    const { timezone, orderTypesConfigV2 } = this.order?.dispensary || {};

    return getCheckoutReservationOptionsHelper({
      reservationsAvailableByOrderType: this.reservationsAvailableByOrderType || {},
      timezone: timezone ?? '',
      orderTypesConfigV2,
      orderType: this.orderType ?? '',
      deliveryAreaId,
    });
  }

  @computed get apt(): string | null {
    if (this.address.ln2) {
      return this.address.ln2;
    }
    if (this.address.apt) {
      return this.address.apt;
    }
    return null;
  }

  @computed get formattedAddress(): string {
    // TODO: any more checking necessary here?
    // see consumer-webpack/src/state/ui.js for reference
    if (_.isEmpty(this.address)) {
      return '';
    }

    return formatAddress(this.address);
  }

  @computed get formattedAddressShort(): string {
    if (_.isEmpty(this.address)) {
      return '';
    }

    return formatAddressShort(this.address);
  }

  @computed get formattedGuestCustomerBirthdate(): string {
    if (!this.guestCustomer) return '';
    return _.compact(
      _.reject([this.guestCustomer.birthMonth, this.guestCustomer.birthDay, this.guestCustomer.birthYear], isBlank)
    ).join('/');
  }

  @computed get menuType(): string {
    if (this.isMedical) {
      return MENU_TYPE_MED;
    }
    return MENU_TYPE_REC;
  }

  @computed get hasAddress(): boolean {
    const addressLine1 = this.address?.ln1 || '';
    const hasAddressLine = !_.isEmpty(addressLine1) || /^\d+$/.test(addressLine1.split(' ')[0]);
    const hasLocationLng = this.address?.lng;
    const hasNoAddress = !hasLocationLng && !hasAddressLine;
    if (hasNoAddress) {
      return false;
    }
    return true;
  }

  @computed get combineDiscounts(): boolean {
    const type = _.toLower(this.rewardsType);
    const integration = this.order.dispensary?.consumerDispensaryIntegrations;
    const { enabled, flags } = integration?.[type] ?? {};
    if (enabled) {
      return !!flags?.combineDiscounts;
    }
    return false;
  }

  @computed get rewardsType(): string | undefined {
    const { consumerDispensaryIntegrations = null } = this.order.dispensary ?? {};
    const { rewards } = integrationsByCategory;
    const rewardProgram = _.find(_.keys(rewards), (program) => !!consumerDispensaryIntegrations?.[program]?.enabled);
    return rewardProgram ? rewards[rewardProgram] : undefined;
  }

  @computed get rewardsProgramDisplayName(): string {
    const { dispensary } = this.order;
    const location = dispensary?.location ?? undefined;
    if (this.rewardsProgramEnabled) {
      return (
        dispensary?.storeSettings?.rewardsIntegrationConfiguration?.rewardsProgramDisplayName ||
        getDefaultRewardsProgramDisplayName({ location })
      );
    }
    return getDefaultRewardsProgramDisplayName({ location });
  }

  @computed get rewardsProgramDisplayDescription(): string {
    const { dispensary } = this.order;
    const location = dispensary?.location ?? undefined;
    if (this.rewardsProgramEnabled) {
      return (
        dispensary?.storeSettings?.rewardsIntegrationConfiguration?.rewardsProgramDisplayDescription ||
        getDefaultRewardsProgramDisplayDescription({ location })
      );
    }
    return getDefaultRewardsProgramDisplayDescription({ location });
  }

  @computed get rewardsProgramDisplayEnrollment(): string {
    const { dispensary } = this.order;
    const location = dispensary?.location ?? undefined;
    if (this.rewardsProgramEnabled) {
      return (
        dispensary?.storeSettings?.rewardsIntegrationConfiguration?.rewardsProgramDisplayEnrollment ||
        getDefaultRewardsProgramDisplayEnrollment({ location })
      );
    }
    return getDefaultRewardsProgramDisplayEnrollment({ location });
  }

  @computed get rewardsProgramEnabled(): boolean {
    const { dispensary } = this.order;
    const { consumerDispensaryIntegrations = null } = dispensary ?? {};
    const adapters = _.keys(integrationsByCategory.rewards);
    return _.some(adapters, (program) => !!consumerDispensaryIntegrations?.[program]?.enabled);
  }

  @computed get hasMedOnlyProducts(): boolean {
    return !!_.find(this.items, `product.medicalOnly`);
  }

  @computed get hasRecOnlyProducts(): boolean {
    return !!_.find(this.items, `product.recOnly`);
  }

  //  non-isolated menu items can have recOnly or medicalOnly attributes
  @computed get hasMenuTypeInclusiveProducts(): boolean {
    return _.some(this.items, (item) => !item?.product?.medicalOnly && !item?.product?.recOnly);
  }

  @computed get cartItemKeys(): string[] {
    return _.keys(this.items);
  }

  @computed
  get hasDiscounts(): boolean {
    return _.some(
      this.costBreakdown.receipt?.details?.products,
      (item) =>
        (item.discounts?.sale ||
          item.discounts?.SALE ||
          item.discounts?.bogo ||
          item.discounts?.OFFER ||
          item.discounts?.all) &&
        (item.discounts?.total ?? 0) > 0
    );
  }
}
